import React, {Fragment, memo, useCallback, useEffect, useRef, useState} from "react"
import {
    ALARM_VALUE_HOME, ALARM_VALUE_OUTSIDE,
    CONTROL_TYPE_ID_ALARM, CONTROL_TYPE_ID_ANALOG,
    CONTROL_TYPE_ID_DIMMER, CONTROL_TYPE_ID_ELEVATOR,
    CONTROL_TYPE_ID_DIMMER_COLOR,
    CONTROL_TYPE_ID_THERMOSTAT,
    ControlModel,
    ControlTypeModel,
    MEDIA_TYPE_ID_AIR_CONDITIONER,
    MediaModel
} from "../../types/control"
import {useForceUpdate} from "../../hooks/force-update"
import {toggleScenarioScreenAction} from "../../store/ui/actions"

import "./index.scss"

import iconMinus from "../../assets/images/icons/controls/minus.png"
import iconPlus from "../../assets/images/icons/controls/plus.png"
import iconUpButton from "../../assets/images/icons/controls/up-button.png"
import iconEqualButton from "../../assets/images/icons/controls/equal-button.png"
import iconDownButton from "../../assets/images/icons/controls/down-button.png"
import iconCancel from "../../assets/images/icons/scenario/cancel.png"
import iconSave from "../../assets/images/icons/scenario/save.png"
import iconUpArrow from "../../assets/images/icons/scenario/up-arrow.png"
import iconDownArrow from "../../assets/images/icons/scenario/down-arrow.png"
import iconClear from "../../assets/images/icons/scenario/clear.png"

import {useKeyboard} from "../../hooks/keyboard"
import {ScenarioModel} from "../../store/control/state"
import {useRPC} from "../../hooks/rpc"
import {useDispatch, useSelector} from "react-redux"
import {usePrevious} from "../../hooks/previous"
import {onClickHandler} from "../../utils/onclick"
import {useSetSelector} from "../../hooks/set-selector"
import {useMessage} from "../../hooks/message"
import {mediaIcons} from "../MediaListScreen"
import {useLocalization} from "../../hooks/localization"
import {rgbToHex} from "../../components/ColorPicker/utils/convert"

type DeviceInfo = {
    control: ControlModel;
    controlType: ControlTypeModel<unknown>;
    media: MediaModel;
    value: number;
    color?: any;
};

type OperationInfo = {
    info: DeviceInfo;
    delay: number;
}

// const options = ['Hepsi', 'Oda', 'Kat', 'Tip', 'A-Z'];
const options = ["ALL", "AREA", "TYPE", "A-Z"]
const showTypes = ["is_notification", "show_on_footer"]
const checkSelected = (info: DeviceInfo, value: number) => info.value === value ? "selected" : ""

const colorList = [
    {r: 255, g: 0, b: 0},  //red
    {r: 255, g: 126, b: 0}, //orange
    {r: 255, g: 255, b: 0}, //yellow
    {r: 0, g: 255, b: 0},  //green
    {r: 0, g: 255, b: 162}, //teal
    {r: 0, g: 255, b: 255}, //lightblue
    {r: 0, g: 0, b: 255},   //blue
    {r: 123, g: 0, b: 255}, //deep purple
    {r: 200, g: 0, b: 255}, //purple
    {r: 255, g: 0, b: 217}, //pink
    {r: 255, g: 255, b: 255}, //white
]

const getNumber = (divider: number, delay: number) => {
    const value = Math.floor((delay / divider) % 60)
    if (value === 0) {
        return ""
    }

    let number = value.toFixed(0)
    if (number.length < 1) {
        number = "00"
    } else if (number.length < 2) {
        number = "0" + number
    }

    return number
}

const getDelay = (delay: number, t: (text: string) => string = null) => {

    const sec = getNumber(1, delay)
    const min = getNumber(60, delay)

    let output = ""

    if (min !== "") {
        output += `${min} ${t ? t("minute") : "dk"} `
    }

    if (sec === "") {
        output += `00 ${t ? t("second") : "sn"}`
    } else {
        output += `${sec} ${t ? t("second") : "sn"}`
    }

    return output
}
const getImage = (info: DeviceInfo): string => {
    // if (info.controlType.id === '6') {
    //     return info.controlType.image_set[`default_` + (info.control.settings as MediaDeviceSettings).media_control_icon.replace('-', '_')] ||
    //         info.controlType.image_set.default;
    // } // todo media scenario
    return info.controlType.image_set.default
}
type DeviceProps = {
    info: DeviceInfo;
    setSelected: (info: DeviceInfo, value: number) => void;
    increase: (info: DeviceInfo, min: number, max: number) => void;
    changeColor?: (info: DeviceInfo, isUp: boolean) => void;
};

const SimpleDevice = memo(function SimpleDevice({
                                                    info,
                                                    setSelected,
                                                    isPulse
                                                }: Partial<DeviceProps> & { isPulse?: boolean }) {
    const {t} = useLocalization()
    const setZero = useCallback(() => {
        setSelected(info, 0)
    }, [info, setSelected])
    const setOne = useCallback(() => {
        setSelected(info, 1)
    }, [info, setSelected])

    return (
        <div className="device-body simple">
            {
                renderIf(!isPulse)(() => (
                    <div className={"device-button " + checkSelected(info, 1)} {...onClickHandler(setOne)}>
                        {info.controlType.render_type === "pulse" ? t("CALL") : "I"}
                    </div>
                ))
            }
            {
                renderIf(info.controlType.render_type !== "pulse" || isPulse)(() => (
                    <div className={"device-button " + checkSelected(info, 0)} {...onClickHandler(setZero)}>
                        0
                    </div>
                ))
            }
        </div>
    )
})

const ThreePhaseDevice = memo(function ThreePhaseDevice({info, setSelected}: Partial<DeviceProps>) {
    return (
        <div className="device-body">
            <div className={"device-button " + checkSelected(info, 1)}
                 {...onClickHandler(() => setSelected(info, 1))}>
                <img src={iconUpButton} alt=""/>
            </div>
            {
                renderIf(info.controlType.render_type === "three_phase")(() => (
                    <div className={"device-button " + checkSelected(info, 0)}
                         {...onClickHandler(() => setSelected(info, 0))}>
                        <img src={iconEqualButton} alt=""/>
                    </div>
                ))
            }
            <div className={"device-button " + checkSelected(info, 2)}
                 {...onClickHandler(() => setSelected(info, 2))}>
                <img src={iconDownButton} alt=""/>
            </div>
        </div>
    )
})

const Device = memo(function Device({info, setSelected, increase, changeColor}: DeviceProps) {
    const {t} = useLocalization()
    if (info.media != null) {
        if (info.media.type_id === MEDIA_TYPE_ID_AIR_CONDITIONER) {
            return (
                <div className="device-body simple">
                    <div className={"device-button " + checkSelected(info, 1)}
                         {...onClickHandler(() => setSelected(info, 1))}>
                        I
                    </div>
                    <div className={"device-button " + checkSelected(info, 0)}
                         {...onClickHandler(() => setSelected(info, 0))}>
                        0
                    </div>
                </div>
            )
        } else {
            return (
                <div className="device-body simple">
                    <div className="device-button selected">
                        I/0
                    </div>
                </div>
            )
        }
    }

    if (info.controlType.render_type === "simple" || info.controlType.render_type === "pulse") {
        return (
            <SimpleDevice info={info} setSelected={setSelected}/>
        )
    }

    if (info.controlType.render_type === "three_phase" || info.controlType.render_type === "two_phase") {
        return (
            <ThreePhaseDevice info={info} setSelected={setSelected}/>
        )
    }

    switch (info.controlType.id) {
        case CONTROL_TYPE_ID_DIMMER:
            return (
                <div className="device-body">
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, -10, 0))}>
                        <img src={iconMinus} alt=""/>
                    </div>
                    <div className="device-digit">
                        {info.value}
                    </div>
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, 10, 100))}>
                        <img src={iconPlus} alt=""/>
                    </div>
                </div>
            )
        case CONTROL_TYPE_ID_ANALOG:
            return (
                <div className="device-body">
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, -10, 0))}>
                        <img src={iconMinus} alt=""/>
                    </div>
                    <div className="device-digit">
                        {info.value}
                    </div>
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, 10, 100))}>
                        <img src={iconPlus} alt=""/>
                    </div>
                </div>
            )
        case "401":
            return (
                <div className="device-body">
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, -10, 0))}>
                        <img src={iconMinus} alt=""/>
                    </div>
                    <div className="device-digit">
                        {info.value}
                    </div>
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, 10, 100))}>
                        <img src={iconPlus} alt=""/>
                    </div>
                </div>
            )
        case "402":
            return (
                <div className="device-body">
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, -10, 0))}>
                        <img src={iconMinus} alt=""/>
                    </div>
                    <div className="device-digit">
                        {info.value}
                    </div>
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, 10, 100))}>
                        <img src={iconPlus} alt=""/>
                    </div>
                </div>
            )
        case CONTROL_TYPE_ID_DIMMER_COLOR:
            return (
                <div className="device-body">
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, -10, 0))}>
                        <img src={iconMinus} alt=""/>
                    </div>
                    <div className="device-digit" style={{display: "flex", justifyContent: "center", width: 54}}>
                        {info.value || 0}
                    </div>
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, 10, 100))}>
                        <img src={iconPlus} alt=""/>
                    </div>
                    <div className="device-button"
                         {...onClickHandler(() => changeColor(info, true))}>
                        <img src={iconUpButton} alt=""/>
                    </div>
                    <div className="device-button" style={{backgroundColor: rgbToHex(info.color || colorList[0])}}/>
                    <div className="device-button"
                         {...onClickHandler(() => changeColor(info, false))}>
                        <img src={iconDownButton} alt=""/>
                    </div>
                </div>
            )
        case CONTROL_TYPE_ID_ALARM:
            return (
                <div className="device-body simple">
                    <div className={"device-button " + checkSelected(info, ALARM_VALUE_HOME)}
                         {...onClickHandler(() => setSelected(info, ALARM_VALUE_HOME))}>
                        {t("HOME")}
                    </div>
                    <div className={"device-button " + checkSelected(info, ALARM_VALUE_OUTSIDE)}
                         {...onClickHandler(() => setSelected(info, ALARM_VALUE_OUTSIDE))}>
                        {t("OUT")}
                    </div>
                </div>
            )
        // case '6':
        //     return (
        //         <SimpleDevice
        //             info={info}
        //             setSelected={setSelected}
        //             isPulse={(info.control.settings as MediaDeviceSettings).media_control_type_id !== '1'}
        //         />
        //     ); // todo media scenario
        case CONTROL_TYPE_ID_THERMOSTAT:
            return (
                <div className="device-body">
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, -5, 155))}>
                        <img src={iconMinus} alt=""/>
                    </div>
                    <div className="device-digit">
                        {(info.value / 10).toFixed(1)}
                    </div>
                    <div className="device-button"
                         {...onClickHandler(() => increase(info, 5, 320))}>
                        <img src={iconPlus} alt=""/>
                    </div>
                </div>
            )
    }

    return (
        <div className="device-body">

        </div>
    )
})

const SimpleScenario = memo(function SimpleScenario({
                                                        scenario,
                                                        isPulse
                                                    }: { scenario: OperationInfo; isPulse?: boolean }) {
    const {t} = useLocalization()
    return (
        <div className="device-body">
            {
                renderIf(!isPulse)(() => (
                    <div className={"device-button " + checkSelected(scenario.info, 1)}>
                        {scenario.info.controlType.render_type === "pulse" ? t("CALL") : "I"}
                    </div>
                ))
            }
            {
                renderIf(scenario.info.controlType.render_type !== "pulse" || isPulse)(() => (
                    <div className={"device-button " + checkSelected(scenario.info, 0)}>
                        0
                    </div>
                ))
            }
        </div>
    )
})

const ThreePhaseScenario = memo(function SimpleScenario({scenario}: { scenario: OperationInfo }) {
    return (
        <div className="device-body">
            <div className={"device-button " + checkSelected(scenario.info, 1)}>
                <img src={iconUpButton} alt=""/>
            </div>
            {
                renderIf(scenario.info.controlType.render_type === "three_phase")(() => (
                    <div className={"device-button " + checkSelected(scenario.info, 0)}>
                        <img src={iconEqualButton} alt=""/>
                    </div>
                ))
            }
            <div className={"device-button " + checkSelected(scenario.info, 2)}>
                <img src={iconDownButton} alt=""/>
            </div>
        </div>
    )
})

const Scenario = memo(function Scenario({scenario}: { scenario: OperationInfo }) {
    const {t} = useLocalization()
    if (scenario.info.media != null) {
        if (scenario.info.media.type_id === MEDIA_TYPE_ID_AIR_CONDITIONER) {
            return (
                <div className="device-body">
                    <div className={"device-button " + checkSelected(scenario.info, 1)}>
                        I
                    </div>
                    <div className={"device-button " + checkSelected(scenario.info, 0)}>
                        0
                    </div>
                </div>
            )
        } else {
            return (
                <div className="device-body">
                    <div className="device-button selected">
                        I/0
                    </div>
                </div>
            )
        }
    }

    if (scenario.info.controlType.render_type === "simple" || scenario.info.controlType.render_type === "pulse") {
        return (
            <SimpleScenario scenario={scenario}/>
        )
    }

    if (scenario.info.controlType.render_type === "three_phase" || scenario.info.controlType.render_type === "two_phase") {
        return (
            <ThreePhaseScenario scenario={scenario}/>
        )
    }

    switch (scenario.info.controlType.id) {
        case CONTROL_TYPE_ID_DIMMER:
            return (
                <div className="device-body">
                    <div className="device-digit">
                        {scenario.info.value}
                    </div>
                </div>
            );
        case CONTROL_TYPE_ID_DIMMER_COLOR:
            return (
                <div className="device-body">
                    <div className="device-digit">
                        {scenario.info.value}
                    </div>
                    <div className="device-color-button"
                         style={{
                             backgroundColor: rgbToHex(scenario.info.color || colorList[0])
                         }}/>
                </div>
            )
        case CONTROL_TYPE_ID_ANALOG:
            return (
                <div className="device-body">
                    <div className="device-digit">
                        {scenario.info.value}
                    </div>
                </div>
            );
        case "300":
            return (
                <div className="device-body">
                    <div className="device-digit">
                        {scenario.info.value}
                    </div>
                </div>
            );
        case "400":
            return (
                <div className="device-body">
                    <div className="device-digit">
                        {scenario.info.value}
                    </div>
                </div>
            );
        case CONTROL_TYPE_ID_ALARM:
            return (
                <div className="device-body">
                    <div className={"device-button " + checkSelected(scenario.info, ALARM_VALUE_HOME)}>
                        {t("HOME")}
                    </div>
                    <div className={"device-button " + checkSelected(scenario.info, ALARM_VALUE_OUTSIDE)}>
                        {t("OUT")}
                    </div>
                </div>
            )
        // case '6':
        //     return (
        //         <SimpleScenario
        //             scenario={scenario}
        //             isPulse={(scenario.info.control.settings as MediaDeviceSettings).media_control_type_id !== '1'}
        //         />
        //     ); // todo scenario media
        case CONTROL_TYPE_ID_THERMOSTAT:
            return (
                <div className="device-body">
                    <div className="device-digit">
                        {(scenario.info.value / 10).toFixed(1)}
                    </div>
                </div>
            )
    }

    return (
        <div className="device-body">

        </div>
    )
})

export const ScenarioCreateScreen = memo(function ScenarioCreateScreen() {
    const [rpc] = useRPC()

    const {t} = useLocalization()
    const inputRef = useRef<HTMLInputElement>()
    const [setActiveInput, renderKeyboard] = useKeyboard()
    const [scenarioName, setScenarioName] = useState("")
    const [isNotification, setIsNotification] = useState(false)
    const [showOnFooter, setShowOnFooter] = useState(false)
    const [scenarioId, setScenarioId] = useState("")
    const {success, failure} = useMessage()
    const dispatch = useDispatch()

    const mediaList = useSelector(state => state.control.mediaList)
    const controlList = useSelector(state => state.control.controlList)
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList)
    const selectedScenario = useSelector(state => state.ui.scenarioScreen.selectedScenario)
    const [selectedOrderOption, setSelectedOrderOption] = useState(0)
    const [defaultColor, setDefaultColor] = useState(colorList[0]);

    const previousSelectedOrderOption = usePrevious(selectedOrderOption)
    const [devices, setDevices] = useState<DeviceInfo[]>([{
        control: null,
        controlType: controlTypeSet[CONTROL_TYPE_ID_ALARM],
        media: null,
        value: null
    }])

    const [operations, setOperations] = useState<OperationInfo[]>([])

    const forceUpdate = useForceUpdate()

    useEffect(() => {
        let newDevices = [...devices]
        let changed = false
        for (const control of controlList) {
            if (!(controlTypeSet[control.type_id] && controlTypeSet[control.type_id].show_on_scenario)) {
                continue
            }

            const hasDevice = newDevices.some(d => d.media == null && d.control != null && d.control.id === control.id)
            if (!hasDevice) {
                let value = null

                if (control.type_id === CONTROL_TYPE_ID_DIMMER) {
                    value = 50
                } else if (control.type_id === CONTROL_TYPE_ID_THERMOSTAT) {
                    value = 250;
                } else if (control.type_id === CONTROL_TYPE_ID_ANALOG) {
                    value = 50;
                }else if (control.type_id === "401") {
                    value = 50;
                }else if (control.type_id === "402") {
                    value = 50
                } else if (control.type_id === CONTROL_TYPE_ID_ELEVATOR) {
                    value = 1
                    // } else if (control.type_id === '6') {
                    //     if ((control.settings as MediaDeviceSettings).media_control_type_id !== '1') {
                    //         value = 0;
                    //     } // todo media scenario
                }

                newDevices.push({
                    control: control,
                    controlType: controlTypeSet[control.type_id],
                    media: null,
                    value
                })
                changed = true
            }
        }
        for (const media of mediaList) {
            const hasDevice = newDevices.some(d => d.media?.id === media.id)
            if (!hasDevice) {
                newDevices.push({
                    control: null,
                    controlType: null,
                    media,
                    value: media.type_id === MEDIA_TYPE_ID_AIR_CONDITIONER ? null : 0
                })
                changed = true
            }

        }
        if (changed || previousSelectedOrderOption !== selectedOrderOption) {
            newDevices = newDevices.sort((a, b) => {
                if (selectedOrderOption === 1) { // Alan
                    if (a.media != null && b.media != null) {
                        return 0
                    } else if (a.media != null) {
                        return 1
                    } else if (b.media != null) {
                        return -1
                    }

                    if (a.control == null) {
                        return -1
                    }

                    if (b.control == null) {
                        return 1
                    }

                    return a.control.area_id.localeCompare(b.control.area_id)
                }

                if (selectedOrderOption === 2) { // Tip
                    if (a.media != null && b.media != null) {
                        return 0
                    } else if (a.media != null) {
                        return 1
                    } else if (b.media != null) {
                        return -1
                    }

                    if (a.control == null) {
                        return -1
                    }

                    if (b.control == null) {
                        return 1
                    }

                    return a.control.type_id.localeCompare(b.control.type_id)
                }

                if (selectedOrderOption === 3) { // A-Z


                    if (a.media == null && a.control == null) {
                        return -1
                    }

                    if (b.media == null && b.control == null) {
                        return 1
                    }

                    const aName = a.control?.name || a.media?.name || ""
                    const bName = b.control?.name || b.media?.name || ""
                    return aName.localeCompare(bName)
                }

                if (a.media != null && b.media != null) {
                    return 0
                } else if (a.media != null) {
                    return 1
                } else if (b.media != null) {
                    return -1
                }

                if (a.media == null && a.control == null) {
                    return -1
                }

                if (b.media == null && b.control == null) {
                    return 1
                }

                return a.control.id.localeCompare(b.control.id) // todo temporary
            })
            changed = true
        }

        if (changed) {
            setDevices(newDevices)
        }
    }, [devices, controlList, selectedOrderOption, previousSelectedOrderOption, controlTypeSet, mediaList])

    useEffect(() => {
        if (selectedScenario == null) {
            return
        }

        if (operations.length === selectedScenario.operation_list.length) {
            // Scenario was already loaded, so return
            return
        }

        setScenarioName(selectedScenario.name)
        setScenarioId(selectedScenario.id)
        setIsNotification(selectedScenario.is_notification)
        setShowOnFooter(selectedScenario.show_on_footer)
        if (devices && devices.length) {
            const operationList = []
            for (const operation of selectedScenario.operation_list) {
                const deviceInfo = devices.find(device => device.control?.id === operation.relational_id || device.media?.id === operation.relational_id || operation.relational_id === "alarm" || false)
                if (deviceInfo == null) {
                    // todo should show error?
                    continue
                }

                const operationInfo: OperationInfo = {
                    delay: operation.delay_seconds,
                    info: {
                        control: deviceInfo.control,
                        controlType: deviceInfo.controlType,
                        media: deviceInfo.media,
                        value: operation.value,
                        color: operation.rgb_color

                    }
                }

                operationList.push(operationInfo)
            }

            setOperations(operationList)
        }
        // eslint-disable-next-line
    }, [selectedScenario, devices])

    const setSelected = useCallback((info: DeviceInfo, value: number) => {
        info.value = value
        forceUpdate()
    }, [forceUpdate])

    const increase = useCallback((info: DeviceInfo, diff: number, limit: number) => {
        let value: number = info.value
        value += diff

        if (diff > 0) {
            if (value > limit) {
                value = limit
            }
        } else {
            if (value < limit) {
                value = limit
            }
        }

        info.value = value
        forceUpdate()
    }, [forceUpdate])

    const changeColor = useCallback((info: DeviceInfo, isUp: boolean) => {
        const oldColor = info.color || colorList[0]
        let newIndex = colorList.findIndex(el => el.r === oldColor.r && el.g === oldColor.g && el.b === oldColor.b) + (isUp ? 1 : -1)
        if (newIndex === -1) newIndex = colorList.length - 1
        if (newIndex === colorList.length) newIndex = 0
        info.color = colorList[newIndex]
        forceUpdate()
    }, [forceUpdate])

    const add = useCallback((info: DeviceInfo) => {
        if (info.value == null) {
            return
        }
        if(info.color === undefined){
            info.color = defaultColor
        }
        setDefaultColor(info.color);
        setOperations(operations => ([...operations, {info: {...info}, delay: 0}]))
    }, [defaultColor])

    const move = useCallback((info: OperationInfo, diff: number) => {
        const index = operations.indexOf(info)
        if (index === 0 && diff < 0) {
            return
        } else if (index === (operations.length - 1) && diff > 0) {
            return
        }

        const newScenarios = [...operations]
        newScenarios.splice(index, 1)
        newScenarios.splice(index + diff, 0, info)

        setOperations(newScenarios)
    }, [operations])

    const remove = useCallback((info: OperationInfo) => {
        setOperations(operations.filter(i => i !== info))
    }, [operations])

    const onFocus = useCallback(() => {
        setActiveInput([inputRef, scenarioName, setScenarioName])
    }, [scenarioName, setActiveInput])

    const onBlur = useCallback(() => {
        setActiveInput(null)
    }, [setActiveInput])

    const close = useCallback(() => dispatch(toggleScenarioScreenAction(null)), [dispatch])

    const save = useCallback(() => {
        if (!scenarioName || !scenarioName.trim()) {
            failure(t("PLS_ENTER_SCENARIO_NAME"))

            return
        }

        if (!operations || !operations.length) {
            failure(t("ACTION_LIST_CANNOT_BE_EMPTY"))

            return
        }
        const scenario: ScenarioModel = {
            id: scenarioId,
            name: scenarioName,
            is_notification: isNotification,
            show_on_footer: showOnFooter,
            operation_list: operations.map(s => ({
                type: s.info.media != null ? "media" : "control",
                relational_id: s.info.media != null ? s.info.media.id : (s.info.control != null ? s.info.control.id : "alarm"),
                value: s.info.value,
                delay_seconds: s.delay,
                rgb_color: s.info.color
            }))
        }
        console.log(scenario)
        rpc.updateScenario(scenario)
            .then(isSucceeded => {
                if (isSucceeded) {
                    success(t(scenarioId.length === 0 ? "SCENARIO_SUCCESSFULLY_SAVED" : "SCENARIO_SUCCESSFULLY_EDITED"))
                    close()
                } else {
                    return Promise.reject()
                }
            })
            .catch(failure)
    }, [t, close, operations, rpc, scenarioId, isNotification, showOnFooter, scenarioName, success, failure])

    const addDelay = useCallback((operation: OperationInfo, delay: number) => {
        let newDelay = operation.delay + delay
        if (newDelay < 0) {
            newDelay = 0
        }

        if (newDelay > 300) {
            newDelay = 300
        }

        operation.delay = newDelay
        forceUpdate()
    }, [forceUpdate])

    return (
        <>
            <div className="scenario">
                <div className="content-divider">
                    <div className="content-wrapper-parent content-wrapper-left">
                        <div className="content-wrapper-left top-part">
                            <div className="actions">
                                <input
                                    maxLength={20}
                                    placeholder={t("ENTER_SCENARIO_NAME")}
                                    ref={inputRef}
                                    onChange={e => setScenarioName(e.target.value)}
                                    onFocus={onFocus} onBlur={onBlur}
                                    value={scenarioName}/>
                                <div className="content-actions">
                                    <div className="action" {...onClickHandler(close)}>
                                        <img src={iconCancel} alt=""/>
                                        <span>{t("CANCEL")}</span>
                                    </div>
                                    <div className="action" {...onClickHandler(save)}>
                                        <img src={iconSave} alt=""/>
                                        <span>{t("SAVE")}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="content-settings-bottom-part mb-10 justify-content-left">
                                {
                                    showTypes.map((option, index) => (
                                        <div key={`show_type_option_${index}`}>
                                            {
                                                option === "is_notification" && <div
                                                    className="option mr-30"
                                                    {...onClickHandler(() => setIsNotification(!isNotification))}>
                                                    <span>{t(option)}</span>
                                                    <div className="selection">
                                                        {
                                                            renderIf(isNotification)(() =>
                                                                <div className="selected"/>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            }
                                            {
                                                option === "show_on_footer" && <div key={`show_type_option_${index}`}
                                                                                    className="option"
                                                                                    {...onClickHandler(() => setShowOnFooter(!showOnFooter))}>
                                                    <span>{t(option)}</span>
                                                    <div className="selection">
                                                        {
                                                            renderIf(showOnFooter)(() =>
                                                                <div className="selected"/>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    ))
                                }
                            </div>
                            <div className="content-settings-bottom-part rounded">
                                {
                                    options.map((option, index) => (
                                        <div key={`option_${index}`}
                                             className="option"
                                             {...onClickHandler(() => setSelectedOrderOption(index))}>
                                            <span>{t(option)}</span>
                                            <div className="selection">
                                                {
                                                    renderIf(selectedOrderOption === index)(() =>
                                                        <div className="selected"/>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                        <div className="content-wrapper-left devices">
                            {
                                devices.map(info => (
                                    <div className="device" key={info.control?.id || info.media?.id || "alarm"}>
                                        <div className="device-name">
                                            {info.control?.name || info.media?.name || "Alarm"}
                                        </div>
                                        {
                                            info.controlType?.id !== CONTROL_TYPE_ID_DIMMER_COLOR &&
                                            <img
                                                src={info.media != null ?
                                                    mediaIcons[info.media.icon] :
                                                    getImage(info)}
                                                alt=""/>
                                        }
                                        <Device info={info} increase={increase} changeColor={changeColor}
                                                setSelected={setSelected}/>
                                        <div className="add-button" {...onClickHandler(() => add(info))}>
                                            {t("ADD")}
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    <div className="content-wrapper-parent content-wrapper-right devices scenarios">
                        {
                            operations.map((scenario, index) => (
                                <Fragment key={index}>
                                    <div className="device delay">
                                        <div className="device-name">
                                            {t("DELAY_DURATION")}
                                        </div>
                                        <div className="edit-button" {...onClickHandler(() => addDelay(scenario, -10))}>
                                            -10
                                        </div>
                                        <div className="edit-button" {...onClickHandler(() => addDelay(scenario, -1))}>
                                            -1
                                        </div>
                                        <div className="time">
                                            {getDelay(scenario.delay, t)}
                                        </div>
                                        <div className="edit-button" {...onClickHandler(() => addDelay(scenario, 1))}>
                                            +1
                                        </div>
                                        <div className="edit-button" {...onClickHandler(() => addDelay(scenario, 10))}>
                                            +10
                                        </div>
                                    </div>
                                    <div className="device">
                                        <div className="device-name">
                                            {(scenario.info.control == null && scenario.info.media == null) ? "Alarm" : (scenario.info.control?.name || scenario.info.media?.name || "")}
                                        </div>
                                        <img
                                            src={scenario.info.controlType?.image_set.default || mediaIcons[scenario.info.media?.icon]}
                                            alt=""/>
                                        <Scenario scenario={scenario}/>
                                        <div className="scenario-buttons">
                                            <div className="add-button" {...onClickHandler(() => move(scenario, -1))}>
                                                <img src={iconUpArrow} alt=""/>
                                            </div>
                                            <div className="add-button" {...onClickHandler(() => move(scenario, 1))}>
                                                <img src={iconDownArrow} alt=""/>
                                            </div>
                                            <div className="add-button" {...onClickHandler(() => remove(scenario))}>
                                                <img src={iconClear} alt=""/>
                                            </div>
                                        </div>
                                    </div>
                                </Fragment>
                            ))
                        }
                    </div>
                </div>
            </div>
            {
                renderKeyboard()
            }
        </>
    )
})
